import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Eye, EyeOff, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import BlogEditorComponent from "@/components/BlogEditor";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const slugify = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BlogEditorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const { isAdmin } = useAuth();

  const draftKey = editId ? `blog_draft_${editId}` : "blog_draft_new";

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(draftKey);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };

  const draft = !editId ? loadDraft() : null;

  const [title, setTitle] = useState(draft?.title || "");
  const [earthDate, setEarthDate] = useState(
    draft?.earthDate || `Earth Date: ${new Date().toISOString().split("T")[0]}`
  );
  const [tags, setTags] = useState(draft?.tags || "");
  const [body, setBody] = useState(draft?.body || "");
  const [published, setPublished] = useState(draft?.published || false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editId);

  // Autosave to localStorage every 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify({ title, earthDate, tags, body, published }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [title, earthDate, tags, body, published, draftKey]);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      const { data } = await (supabase.from("blog_posts") as any)
        .select("*")
        .eq("id", editId)
        .single();
      if (data) {
        setTitle(data.title);
        setEarthDate(data.earth_date || "");
        setTags((data.tags || []).join(", "));
        setBody(data.body || "");
        setPublished(data.published ?? false);
      }
      setLoaded(true);
    })();
  }, [editId]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Admin access required
      </div>
    );
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        toast.error("Authentication error — please log in again");
        setSaving(false);
        return;
      }

      const tagArray = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const basePayload = {
        title: title.trim(),
        earth_date: earthDate.trim(),
        body,
        tags: tagArray,
        published,
        author_id: userData.user.id,
        updated_at: new Date().toISOString(),
      };

      // Only set slug on new posts to avoid unique constraint violations
      const payload = editId
        ? basePayload
        : { ...basePayload, slug: slugify(title) + `-${Date.now().toString(36)}` };

      let error;
      if (editId) {
        ({ error } = await (supabase.from("blog_posts") as any)
          .update(payload)
          .eq("id", editId));
      } else {
        ({ error } = await (supabase.from("blog_posts") as any).insert(payload));
      }

      if (error) {
        console.error("Blog save error:", error);
        toast.error("Save failed: " + error.message);
      } else {
        localStorage.removeItem(draftKey);
        toast.success(published ? "Published!" : "Draft saved!");
        navigate("/blog");
      }
    } catch (err: any) {
      console.error("Blog save exception:", err);
      toast.error("Save failed: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) return;
    if (!confirm("Delete this post permanently?")) return;
    await (supabase.from("blog_posts") as any).delete().eq("id", editId);
    toast.success("Post deleted");
    navigate("/blog");
  };

  if (!loaded) return null;

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6 pb-24">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/blog")}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          {editId && (
            <button
              onClick={handleDelete}
              className="p-2 rounded-lg text-destructive hover:bg-destructive/10 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setPublished(!published)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              published
                ? "bg-green-500/15 text-green-500"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {published ? "Public" : "Draft"}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 transition"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <Input
          placeholder="Post title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="text-lg font-semibold"
        />
        <Input
          placeholder="Earth Date: 2026-03-09"
          value={earthDate}
          onChange={(e) => setEarthDate(e.target.value)}
        />
        <Input
          placeholder="Tags (comma separated): update, feature, behind-the-scenes"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
        />
        <BlogEditorComponent content={body} onChange={setBody} />
      </div>
    </div>
  );
};

export default BlogEditorPage;
